import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { IAgentModel } from '../models/loyalty/IAgent.model';
import { IBonusPointsModel } from '../models/loyalty/IBonusPoints.model';
import { IProfileModel } from '../models/loyalty/IProfile.model';
import { IStatusPointsModel } from '../models/loyalty/IStatus-Points.model';
import { isOn } from 'feature-toggle-service';
import { ConfigService } from '@uxdf/direct-shared-lib';

@Injectable({
  providedIn: 'root'
})
export class BackendLoyaltyService {
  url = {
    // @loyaltyDocs: Direct API endpoints

    // checks account id of the loyalty system (java api)
    getAccount: () => 'api/amadeus/loyalty/agentAccount',

    // requests registration in the loyalty system (java api)
    bindAgent: () => 'api/amadeus/loyalty/bindAgent',

    // registers the user in the loyalty system (java api)
    registerAgent: () => 'api/amadeus/loyalty/registerAgent',

    // return a list of bonus points by user (java api)
    bonusPoints: () => 'api/amadeus/loyalty/bonuspoints',

    statusPoints: () => 'api/amadeus/loyalty/statuspoints',

    // Approve requests for access to loyalty for self and child hierarchy
    approveForHierarchy: agencyCAN => `api/amadeus/accountHierarchy/enableLoyalty?can=${agencyCAN}`,

    // Request for loyalty access to nearest DM in parent hierarchy
    requestToHierarchy: agencyCAN => `api/amadeus/accountHierarchy/requestLoyalty?can=${agencyCAN}`,

    // @loyaltyDocs: Magento endpoints

    // return user data
    customer: storeCode => 'amadeus/private/customer/profile',

    // Approve requests for access to loyalty
    approve: 'amadeus/private/loyalty/enable',

    // Request for access to loyalty
    request: 'amadeus/private/loyalty/request'
  };

  private readonly fields = [
    'number',
    'password',
    'contactId',
    'titleId',
    'titleValues',
    'firstName',
    'lastName',
    'address',
    'city',
    'postalCode',
    'countryCode',
    'email',
    'phone',
    'phoneMobile',
    'officeId'
  ];

  constructor(
    private readonly httpClient: HttpClient,
    private readonly initService: ConfigService
  ) {}

  getAccount(): Observable<number> {
    return this.httpClient.get<number>(this.initService.getEnvConfig().AD_JAVA_API_URL + this.url.getAccount(), {
      responseType: 'json'
    });
  }

  bindAgent(number, password, contactId): Observable<IAgentModel> {
    // return of(mockAgent);

    return this.httpClient.post<IAgentModel>(
      this.initService.getEnvConfig().AD_JAVA_API_URL + this.url.bindAgent(),
      { number, password, contactId },
      { responseType: 'json' }
    );
  }

  registerAgent(data: any): Observable<any> {
    const params = this.registerAgentParams(data);

    return this.httpClient.post<any>(this.initService.getEnvConfig().AD_JAVA_API_URL + this.url.registerAgent(), params, {
      responseType: 'json'
    });
  }

  getLoyaltyBonusPoints(): Observable<IBonusPointsModel> {
    // return of(mockBonusPoints);

    return this.httpClient.get<IBonusPointsModel>(this.initService.getEnvConfig().AD_JAVA_API_URL + this.url.bonusPoints(), {
      responseType: 'json'
    });
  }

  getLoyaltyStatusPoints(countryCode, details, pnrSearch, toDate, fromDate): Observable<IStatusPointsModel> {
    return this.httpClient.post<IStatusPointsModel>(
      this.initService.getEnvConfig().AD_JAVA_API_URL + this.url.statusPoints(),
      { countryCode, details, pnrSearch, toDate, fromDate },
      { responseType: 'json' }
    );
  }

  /**
   * Function to approve loyalty (plus-me) for the self CAN or the whole child hierarchy CANs
   * based on the activation parameter.
   * @param agencyCAN CAN number of the logged in user
   * @param storeCode StoreCode of the current user
   * @returns boolean true or false
   */
 approveForHierarchy(agencyCAN, storeCode): Observable<boolean> {
  const url = this.buildHierarchyUrl(
    this.url.approveForHierarchy(agencyCAN));

  if (isOn('accHierarchyLoyaltyEnabled')) {
    return this.httpClient.get<any>(url, {
      responseType: 'json'
    });
  } else {
    const language = localStorage.getItem('language') || 'en_US';
    const baseUrl = this.initService.getEnvConfig().AD_MAGENTO_URL;
    const urlWithLanguage = `${baseUrl}?language=${language}`;
    return this.httpClient.post<any>(
      urlWithLanguage,
      {},
      {
        responseType: 'json'
      }
    );
  }
}


  /**
   * Function to request loyalty to the DM in current CAN or the nearest DM in the whole parent hierarchy
   * based on the activation parameter.
   * @param agencyCAN Can number of the logged in user
   * @param storeCode Store code of the logged in user
   * @returns boolean true or false
   */
 requestToHierarchy(agencyCAN, storeCode): Observable<boolean> {
 const url = this.buildHierarchyUrl(
    this.url.requestToHierarchy(agencyCAN));
  if (isOn('accHierarchyLoyaltyEnabled')) {
    return this.httpClient.get<any>(url, { responseType: 'json' });
  } else {
    const language = localStorage.getItem('language') || 'en_US';
    const baseUrl = this.initService.getEnvConfig().AD_MAGENTO_URL;
    const urlWithLanguage = `${baseUrl}?language=${language}`;
    return this.httpClient.post<any>(
      urlWithLanguage,
      {},
      {
        responseType: 'json'
      }
    );
  }
}

private buildHierarchyUrl(basePath: string): string {
  const language = localStorage.getItem('language');
  const baseUrl = this.initService.getEnvConfig().AD_JAVA_API_URL + basePath;

  return language ? `${baseUrl}&language=${language}` : baseUrl;
}


  getCustomer(storeCode): Observable<IProfileModel> {
    return this.httpClient.get<any>(this.initService.getEnvConfig().AD_MAGENTO_URL + this.url.customer(storeCode));
  }

  /*Helpers*/

  registerAgentParams(data) {
    const params = this.fields.reduce((acc, k) => ({ ...acc, [k]: data[k] }), {});

    // Mobile phone is required in loyalty REST API, solution is to provide phone if not provided
    // See MMMW2-8070
    if (!params['phoneMobile'] && params['phone']) {
      params['phoneMobile'] = params['phone'];
    }

    return params;
  }

  prepareDataForRegisterAgent({ customerConfig, customerProfileConfig }) {
    const titleLabel = customerProfileConfig.nameTitleOption;
    const titleId= 0;

    return {
      number: 0,
      password: '',
      contactId: customerConfig.contact_id,
      titleId,
      titleValues: titleLabel,
      firstName: customerProfileConfig.firstname,
      lastName: customerProfileConfig.lastname,
      address: customerProfileConfig.address.street,
      city: customerProfileConfig.address.city,
      postalCode: customerProfileConfig.address.postalCode,
      countryCode: customerConfig.country_code,
      email: customerProfileConfig.contactEmail,
      phone: customerProfileConfig.workPhoneNumber,
      phoneMobile: customerProfileConfig.mobilePhoneNumber,
      officeId: customerProfileConfig.address.officeId
    };
  }
}
